import { BrutusCaseRunCreateModel, BrutusTestType } from "@app/api2/api";
import { BPBoxWithHeader } from "@app/components/React/Common/BPBoxWithHeader";
import React, { useEffect } from "react";
import { Button, Grid, GridColumn, GridRow, Header, List, ListHeader, ListItem, Segment, Table, TableBody, TableCell, TableRow } from "@/components/bp/bp-ui";
import { IParameterRenderer } from "../../ParameterRendererAbstracts";
import { ParametersView } from "../../ParametersView";
import { PedigreeGraph } from "./Pedigree/Graph";
import { BPButton } from "@/components/bp/bpbutton";

interface IBrutusReviewProps {
    model: BrutusCaseRunCreateModel;
    onComplete: (values: any) => void;
    router: any;
    parameterRenderer: IParameterRenderer;
}

export const BrutusReview: React.FunctionComponent<IBrutusReviewProps> = (props) => {
    const t = window.translation;
    const hasTwoHypotheses = props.model?.brutusTestType == BrutusTestType.Freeform;
    const hasSingleHypothesis = props.model?.brutusTestType == BrutusTestType.UHR || props.model?.brutusTestType == BrutusTestType.Kinship;

    const selectedReferences = props.model?.references.filter(x => x.selected)
    const selectedReferencesH1 = hasTwoHypotheses ? props.model?.h1.references.filter(x => x.selected) : null
    const selectedReferencesH2 = hasTwoHypotheses ? props.model?.h2.references.filter(x => x.selected) : null
    const model = props.model
    const population = props.model?.populations.find((p) => (p.id === props.model.populationId));

    return <>
        <Grid columns={3}>
            <GridRow>
                <GridColumn>
                    <Header attached="top">{t.caseRun.general}</Header>
                    <Segment attached>
                        <List>
                            <ListItem>
                                <ListHeader>{t.common.population}</ListHeader>
                                {population != null ? population.name : ""}
                            </ListItem>
                        </List>
                    </Segment>
                </GridColumn>
                <GridColumn>
                    {(model.brutusTestType == BrutusTestType.Kinship || model.brutusTestType == BrutusTestType.UHR) && <>
                        <Header attached="top">{t.common.referencePlural}</Header>
                        <Segment attached>
                            <List>
                                {props.model.references.filter((p) => (p.selected)).map((p) => (
                                    <ListItem key={p.id}>{p.name}</ListItem>
                                ))}
                            </List>
                        </Segment>
                    </>
                    }
                    {model.brutusTestType == BrutusTestType.Freeform && <>
                        <Header attached="top">{'H1'}</Header>
                        <Segment attached>
                            <List>
                                {props.model.h1?.references.filter((p) => (p.selected)).map((p) => (
                                    <ListItem key={p.id}>{p.name}</ListItem>
                                ))}
                            </List>
                        </Segment>
                        <Header attached="top">{'H2'}</Header>
                        <Segment attached>
                            <List>
                                {props.model.h2?.references.filter((p) => (p.selected)).map((p) => (
                                    <ListItem key={p.id}>{p.name}</ListItem>
                                ))}
                            </List>
                        </Segment>
                    </>
                    }
                </GridColumn>
                <GridColumn>
                    <Header attached="top">{t.common.parameters}</Header>
                    <Segment attached>
                        <ParametersView parameters={props.model.parameters} parameterRenderer={props.parameterRenderer} model={{ ...props.model, efmCaseId: props.model.efmCaseId, hp: props.model.references }}>
                            {(parameters) => (
                                <Table definition striped>
                                    <TableBody>
                                        {parameters.parameters.map((p) => (
                                            <TableRow key={p.parameter}>
                                                <TableCell>{p.label}</TableCell>
                                                <TableCell>{p.value}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </ParametersView>
                    </Segment>
                </GridColumn>
            </GridRow>
            {hasSingleHypothesis &&
                <GridRow columns={1}>
                    <GridColumn>
                        <BPBoxWithHeader header={t.brutus.pedigree}>
                            <PedigreeGraph readOnly people={selectedReferences?.map(x => { return { displayName: x.name, imported: !x.isUntested, missing: x.isMissingPerson, personId: x.id.toString(), sampleName: x.name, sex: x.isMale ? "M" : "F", systems: [] } }) ?? []}
                                relations={model.relations?.map(x => { return { relationId: `${x.ancestorId}${x.descendantId}`, ancestor: x.ancestorId.toString(), descendant: x.descendantId.toString(), questioned: x.questioned } }) ?? []} />
                        </BPBoxWithHeader>
                    </GridColumn>
                </GridRow>
            }
            {hasTwoHypotheses &&
                <GridRow columns={2}>
                    <GridColumn>
                        <BPBoxWithHeader header={t.brutus.pedigree}>
                            <PedigreeGraph readOnly people={selectedReferencesH1?.map(x => { return { displayName: x.name, imported: !x.isUntested, missing: x.isMissingPerson, personId: x.id.toString(), sampleName: x.name, sex: x.isMale ? "M" : "F", systems: [] } }) ?? []}
                                relations={model.h1?.relations?.map(x => { return { relationId: `${x.ancestorId}${x.descendantId}`, ancestor: x.ancestorId.toString(), descendant: x.descendantId.toString(), questioned: x.questioned } }) ?? []} />
                        </BPBoxWithHeader>
                    </GridColumn>
                    <GridColumn>
                        <BPBoxWithHeader header={t.brutus.pedigree}>
                            <PedigreeGraph readOnly people={selectedReferencesH2?.map(x => { return { displayName: x.name, imported: !x.isUntested, missing: x.isMissingPerson, personId: x.id.toString(), sampleName: x.name, sex: x.isMale ? "M" : "F", systems: [] } }) ?? []}
                                relations={model.h2?.relations?.map(x => { return { relationId: `${x.ancestorId}${x.descendantId}`, ancestor: x.ancestorId.toString(), descendant: x.descendantId.toString(), questioned: x.questioned } }) ?? []} />
                        </BPBoxWithHeader>
                    </GridColumn>
                </GridRow>
            }
        </Grid>

        <Segment basic clearing>
            <BPButton floated="right" type="button" onClick={() => {
                // console.log(props.model)
                props.onComplete(props.model);
            }} positive primary>{window.translation.caseRun.queueRun}</BPButton>
        </Segment>
    </>;
};
